import type { ProductTarget, TargetOptions } from '@/utils/productTarget';

/**
 * What the offering surface does when the picker confirms (task-dx7eahgnvt).
 *
 * ⛔ EXTRACTED SO IT CAN BE EXECUTED BY A TEST RATHER THAN PATTERN-MATCHED. The first version of
 * this logic lived inline in a 4,900-line component's JSX, so the only thing a test could do was
 * regex the handler's source — and codex was right that A BROKEN STATE/PROP CONNECTION WOULD PASS
 * THAT AND STILL LEAVE THE CAPABILITY UNUSABLE. Source text is evidence about spelling, not about
 * behaviour.
 */
export interface PickerConfirmHandlers {
  /** Replaces the surface's selection — the SAME state the toolbar's "Enrich Selected (N)" reads. */
  setExplicitIds: (ids: string[]) => void;
  /** The visible Force re-enrich checkbox, so the picker's choice is shown rather than applied unseen. */
  setForce: (force: boolean) => void;
  close: () => void;
}

/**
 * ⛔⛔ EXPLICIT IDS ONLY — A COHORT IS REFUSED, NOT CONVERTED (task-ny7t1n8v5c).
 * batch-enrich-async filters only by categoryPath while this listing filters on FIVE things, so
 * resolving a cohort here would DROP FOUR FILTERS AND SILENTLY WIDEN — enriching products the
 * operator never had on screen. An earlier draft "handled" the cohort branch for completeness and a
 * sibling test caught it: BEING DEFENSIVELY COMPLETE ABOUT A PATH THAT MUST NOT EXIST IS HOW THE
 * WIDENING GETS WRITTEN.
 * ⇒ Unreachable today (the surface leaves allowCohortSelection at its default of false), so this
 *   THROWS rather than degrading: if it ever fires, the affordance was enabled before an endpoint
 *   could resolve a filter kind-awarely, and that is a bug to see, not to absorb.
 */
export function applyPickerConfirm(
  picked: ProductTarget,
  options: TargetOptions,
  handlers: PickerConfirmHandlers,
): void {
  if (picked.mode !== 'explicit') {
    throw new Error(
      'The enrichment surface cannot target a cohort: the batch endpoint filters only by '
      + 'categoryPath, so the other four listing filters would be silently dropped. Enable this '
      + 'only once the endpoint resolves a filter kind-awarely (task-ny7t1n8v5c).'
    );
  }
  handlers.setExplicitIds(picked.ids);
  // The picker's force choice is the operator's most recent statement of intent, so it wins over
  // whatever the toolbar checkbox held — and it lands on the VISIBLE control rather than being
  // applied invisibly at enrich time.
  handlers.setForce(options.force === true);
  handlers.close();
}
